import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import { api } from "./api";
import type { Capability, CapabilityResult, Lawyer, Matter, ToolPresentation } from "./types";

type FieldKind = "text" | "textarea" | "number" | "date" | "select" | "checkbox";

interface ToolField {
  key: string;
  label: string;
  kind: FieldKind;
  required?: boolean;
  placeholder?: string;
  options?: string[];
  defaultValue?: string | number | boolean;
  preset?: "matter" | "lawyer" | "today" | "yearStart" | "year";
}

interface FirmToolDefinition {
  name: string;
  label: string;
  description: string;
  icon: string;
  actionLabel?: string;
  context?: "accounts";
  confirmationLabel?: string;
  fields: ToolField[];
}

interface FirmToolGroup {
  label: string;
  tools: FirmToolDefinition[];
}

const timeTypes = [
  "Research", "Drafting", "CourtAppearance", "ClientMeeting", "PhoneCall", "Email",
  "Travel", "Review", "Conference", "Negotiation", "Other",
];

const sourceTypes = [
  "Invoice", "Payment", "TrustTxn", "VendorBill", "VendorPayment", "Manual",
  "Adjustment", "PayrollImport", "OpeningBalance", "PeriodClose", "Reversal",
];

const accountRoles = ["Partner", "Associate", "Paralegal", "Assistant", "Billing", "ReadOnly"];
const searchableAccountRoles = ["Admin", ...accountRoles];

const groups: FirmToolGroup[] = [
  {
    label: "Overview",
    tools: [
      { name: "getFirmDashboard", label: "Firm dashboard", description: "Deadlines, events, clients, and team activity.", icon: "⌂", fields: [{ key: "daysAhead", label: "Days ahead", kind: "number", defaultValue: 7 }] },
      { name: "getMatterSummary", label: "Matter summary", description: "Team, billing, deadlines, and activity for a matter.", icon: "◇", fields: [{ key: "matterIdentifier", label: "Matter", kind: "text", required: true, preset: "matter" }] },
      { name: "getLawyerDashboard", label: "Lawyer dashboard", description: "Personal matters, time, events, and deadlines.", icon: "◉", fields: [{ key: "lawyerIdentifier", label: "Lawyer", kind: "text", required: true, preset: "lawyer" }] },
    ],
  },
  {
    label: "Documents & analysis",
    tools: [
      { name: "analyzeMatterDocuments", label: "Analyze matter files", description: "Build a cited summary, timeline, issue map, or contradiction review from indexed matter documents.", icon: "AI", actionLabel: "Run document analysis", fields: [
        { key: "matterIdentifier", label: "Matter", kind: "text", required: true, preset: "matter" },
        { key: "mode", label: "Review type", kind: "select", required: true, defaultValue: "summary", options: ["summary", "timeline", "issues", "contradictions", "custom"] },
        { key: "question", label: "Focus question", kind: "textarea", placeholder: "Optional unless review type is custom" },
        { key: "passageLimit", label: "Source passages", kind: "number", defaultValue: 20 },
      ] },
    ],
  },
  {
    label: "Legal research",
    tools: [
      { name: "searchCourtListenerCases", label: "Search U.S. case law", description: "Search CourtListener opinions with direct source links and case metadata.", icon: "§", actionLabel: "Search CourtListener", fields: [
        { key: "query", label: "Issue, case, citation, or advanced query", kind: "textarea", required: true, placeholder: "What duty does an Ohio premises owner owe an invitee?" },
        { key: "semantic", label: "Natural-language semantic search", kind: "checkbox", defaultValue: true },
        { key: "maxResults", label: "Maximum results", kind: "number", defaultValue: 10 },
      ] },
      { name: "verifyCourtListenerCitations", label: "Check case citations", description: "Resolve citations to CourtListener records and flag ambiguous or missing matches.", icon: "✓", actionLabel: "Check citations", fields: [
        { key: "text", label: "Citations or document passage", kind: "textarea", required: true, placeholder: "Paste one or more case citations, or text containing citations." },
      ] },
      { name: "searchStatutes", label: "Search statutes & regulations", description: "Discover current statutes, codes, regulations, and official sources.", icon: "¶", actionLabel: "Search sources", fields: [
        { key: "jurisdiction", label: "Jurisdiction", kind: "text", required: true, placeholder: "Ohio" },
        { key: "topic", label: "Topic or section", kind: "textarea", required: true },
        { key: "maxResults", label: "Maximum results", kind: "number", defaultValue: 5 },
      ] },
    ],
  },
  {
    label: "Time & billing",
    tools: [
      { name: "logTime", label: "Log time", description: "Record billable or non-billable work without posting to chat.", icon: "◷", actionLabel: "Save time entry", fields: [
        { key: "matterIdentifier", label: "Matter", kind: "text", required: true, preset: "matter" },
        { key: "hours", label: "Hours", kind: "number", required: true, placeholder: "1.5" },
        { key: "type", label: "Activity", kind: "select", required: true, options: timeTypes },
        { key: "date", label: "Date", kind: "date", preset: "today" },
        { key: "description", label: "Description", kind: "textarea", required: true, placeholder: "Work performed" },
        { key: "isBillable", label: "Billable", kind: "checkbox", defaultValue: true },
      ] },
      { name: "getMatterTime", label: "Matter time", description: "Filterable time history for the selected matter.", icon: "▤", fields: [{ key: "matterIdentifier", label: "Matter", kind: "text", required: true, preset: "matter" }] },
      { name: "getUnbilledTime", label: "Unbilled time", description: "Work ready to be included on an invoice.", icon: "◫", fields: [{ key: "matterIdentifier", label: "Matter", kind: "text", required: true, preset: "matter" }] },
      { name: "getLawyerTime", label: "Lawyer time", description: "Time entries for a lawyer and date range.", icon: "◷", fields: [
        { key: "lawyerIdentifier", label: "Lawyer", kind: "text", required: true, preset: "lawyer" },
        { key: "startDate", label: "From", kind: "date", preset: "yearStart" },
        { key: "endDate", label: "To", kind: "date", preset: "today" },
      ] },
    ],
  },
  {
    label: "Invoices",
    tools: [
      { name: "getMatterInvoices", label: "Matter invoices", description: "Sortable invoice history and open balances.", icon: "▧", fields: [{ key: "matterIdentifier", label: "Matter", kind: "text", required: true, preset: "matter" }] },
      { name: "getClientInvoices", label: "Client invoices", description: "Invoices across all matters for a client.", icon: "▦", fields: [{ key: "clientIdentifier", label: "Client name or ID", kind: "text", required: true }] },
      { name: "getInvoiceDetails", label: "Invoice details", description: "Line items, totals, balance, and payment history.", icon: "≡", fields: [{ key: "identifier", label: "Invoice number or ID", kind: "text", required: true }] },
      { name: "generateInvoice", label: "Generate hourly invoice", description: "Create an invoice from unbilled time, costs, and expenses.", icon: "+", actionLabel: "Generate invoice", fields: [
        { key: "matterIdentifier", label: "Matter", kind: "text", required: true, preset: "matter" },
        { key: "dueInDays", label: "Due in days", kind: "number", defaultValue: 30 },
        { key: "notes", label: "Invoice notes", kind: "textarea" },
      ] },
      { name: "billFlatFee", label: "Generate flat-fee invoice", description: "Create a full or progress invoice for a flat-fee matter.", icon: "+", actionLabel: "Generate invoice", fields: [
        { key: "matterIdentifier", label: "Matter", kind: "text", required: true, preset: "matter" },
        { key: "amount", label: "Amount (blank for remaining fee)", kind: "number" },
        { key: "dueInDays", label: "Due in days", kind: "number", defaultValue: 30 },
        { key: "notes", label: "Invoice notes", kind: "textarea" },
        { key: "consumeUnbilledTime", label: "Mark covered time as billed", kind: "checkbox", defaultValue: true },
      ] },
      { name: "recordPayment", label: "Record payment", description: "Apply a client payment to an invoice.", icon: "$", actionLabel: "Record payment", fields: [
        { key: "invoiceIdentifier", label: "Invoice number or ID", kind: "text", required: true },
        { key: "amount", label: "Amount", kind: "number", required: true },
        { key: "method", label: "Method", kind: "select", required: true, options: ["Check", "CreditCard", "BankTransfer", "Cash", "TrustAccount", "Other"] },
        { key: "referenceNumber", label: "Reference number", kind: "text" },
        { key: "notes", label: "Payment notes", kind: "textarea" },
      ] },
    ],
  },
  {
    label: "Financial reports",
    tools: [
      { name: "getProfitAndLoss", label: "Profit & loss", description: "Revenue, expenses, and net income for a period.", icon: "↗", fields: [
        { key: "fromDate", label: "From", kind: "date", preset: "yearStart" },
        { key: "toDate", label: "To", kind: "date", preset: "today" },
      ] },
      { name: "getBalanceSheet", label: "Balance sheet", description: "Assets, liabilities, and equity as of a date.", icon: "=", fields: [{ key: "asOfDate", label: "As of", kind: "date", required: true, preset: "today" }] },
      { name: "getTrialBalance", label: "Trial balance", description: "Debit, credit, and balance detail by account.", icon: "±", fields: [{ key: "asOfDate", label: "As of", kind: "date", required: true, preset: "today" }] },
      { name: "getAccountsReceivableAging", label: "A/R aging", description: "Open client balances grouped by age.", icon: "AR", fields: [{ key: "asOfDate", label: "As of", kind: "date", preset: "today" }] },
      { name: "getAccountsPayableAging", label: "A/P aging", description: "Open vendor balances grouped by age.", icon: "AP", fields: [{ key: "asOfDate", label: "As of", kind: "date", preset: "today" }] },
      { name: "getCostRecoveryPerformance", label: "Cost recovery", description: "Incurred, billed, unbilled, and written-off costs.", icon: "%", fields: [
        { key: "fromDate", label: "From", kind: "date", preset: "yearStart" },
        { key: "toDate", label: "To", kind: "date", preset: "today" },
      ] },
      { name: "generate1099Report", label: "1099 workpaper", description: "Eligible vendor payments for annual 1099 preparation.", icon: "99", fields: [
        { key: "taxYear", label: "Tax year", kind: "number", required: true, preset: "year" },
        { key: "threshold", label: "Minimum amount", kind: "number", defaultValue: 600 },
      ] },
    ],
  },
  {
    label: "Accounting",
    tools: [
      { name: "listChartOfAccounts", label: "Chart of accounts", description: "Filterable account structure and status.", icon: "#", fields: [
        { key: "type", label: "Account type", kind: "select", options: ["Asset", "Liability", "Equity", "Revenue", "Expense"] },
        { key: "activeOnly", label: "Active accounts only", kind: "checkbox", defaultValue: true },
      ] },
      { name: "listJournalEntries", label: "Journal entries", description: "Browse and filter general-ledger activity.", icon: "J", fields: [
        { key: "fromDate", label: "From", kind: "date", preset: "yearStart" },
        { key: "toDate", label: "To", kind: "date", preset: "today" },
        { key: "sourceType", label: "Source", kind: "select", options: sourceTypes },
        { key: "limit", label: "Maximum rows", kind: "number", defaultValue: 100 },
      ] },
      { name: "listVendorBills", label: "Vendor bills", description: "Review bills and balances for a vendor.", icon: "VB", fields: [
        { key: "vendorIdentifier", label: "Vendor name or ID", kind: "text", required: true },
        { key: "status", label: "Status", kind: "select", options: ["Draft", "PendingApproval", "Approved", "PartiallyPaid", "Paid", "Voided"] },
      ] },
      { name: "listBankAccounts", label: "Bank accounts", description: "Operating and trust bank account directory.", icon: "▰", fields: [
        { key: "type", label: "Account type", kind: "select", options: ["Operating", "IOLTAPooled", "TrustSeparate"] },
        { key: "activeOnly", label: "Active accounts only", kind: "checkbox", defaultValue: true },
      ] },
      { name: "getTrustAccountSummary", label: "Trust account summary", description: "Book, cleared, and outstanding trust balances.", icon: "T", fields: [{ key: "bankAccountIdentifier", label: "Bank account name or ID", kind: "text", required: true }] },
      { name: "listTrustTransactions", label: "Trust transactions", description: "Filterable trust ledger activity.", icon: "⇄", fields: [
        { key: "bankAccountIdentifier", label: "Bank account name or ID", kind: "text", required: true },
        { key: "fromDate", label: "From", kind: "date", preset: "yearStart" },
        { key: "toDate", label: "To", kind: "date", preset: "today" },
        { key: "unclearedOnly", label: "Uncleared only", kind: "checkbox", defaultValue: false },
        { key: "limit", label: "Maximum rows", kind: "number", defaultValue: 100 },
      ] },
    ],
  },
  {
    label: "Account management",
    tools: [
      { name: "getAllLawyers", label: "Team members", description: "See everyone who can sign in, including their role and status.", icon: "◎", context: "accounts", fields: [] },
      { name: "getLawyersByRole", label: "Team by role", description: "Find team members assigned to a specific role.", icon: "≡", context: "accounts", fields: [
        { key: "role", label: "Role", kind: "select", required: true, placeholder: "Choose a role", options: searchableAccountRoles },
      ] },
      { name: "getLawyerByEmail", label: "Team member details", description: "Review a person's role, billing rate, license, and access status.", icon: "@", context: "accounts", fields: [
        { key: "email", label: "Name or email", kind: "text", required: true },
      ] },
      { name: "getLawyerMatters", label: "Team member matters", description: "See the matters where a person has recorded time.", icon: "M", context: "accounts", fields: [
        { key: "identifier", label: "Name or email", kind: "text", required: true },
      ] },
      { name: "createLawyer", label: "Add team member", description: "Give a lawyer or staff member access to ParaPlus.", icon: "+", actionLabel: "Add team member", context: "accounts", confirmationLabel: "I have reviewed these details and want to add this team member.", fields: [
        { key: "email", label: "Email", kind: "text", required: true },
        { key: "firstName", label: "First name", kind: "text", required: true },
        { key: "lastName", label: "Last name", kind: "text", required: true },
        { key: "role", label: "Role", kind: "select", required: true, placeholder: "Choose a role", options: accountRoles },
        { key: "defaultHourlyRate", label: "Default hourly rate", kind: "number" },
        { key: "barNumber", label: "Bar number", kind: "text" },
        { key: "licenseState", label: "License state", kind: "text" },
      ] },
      { name: "updateLawyer", label: "Edit team member", description: "Change a person's name or role.", icon: "✎", actionLabel: "Save changes", context: "accounts", confirmationLabel: "I have reviewed these changes and want to save them.", fields: [
        { key: "identifier", label: "Name or email", kind: "text", required: true },
        { key: "firstName", label: "New first name", kind: "text" },
        { key: "lastName", label: "New last name", kind: "text" },
        { key: "role", label: "New role", kind: "select", placeholder: "Leave unchanged", options: accountRoles },
      ] },
      { name: "updateHourlyRate", label: "Update billing rate", description: "Change a user's default hourly billing rate.", icon: "$", actionLabel: "Update rate", context: "accounts", confirmationLabel: "I have reviewed the new rate and want to save it.", fields: [
        { key: "identifier", label: "Name or email", kind: "text", required: true },
        { key: "rate", label: "New hourly rate", kind: "number", required: true },
      ] },
      { name: "deactivateLawyer", label: "Deactivate account", description: "Disable login while preserving the user's history.", icon: "×", actionLabel: "Deactivate account", context: "accounts", confirmationLabel: "I understand this person will no longer be able to sign in.", fields: [
        { key: "identifier", label: "Name or email", kind: "text", required: true },
      ] },
    ],
  },
];

function presetValue(field: ToolField, matter: Matter | null, lawyer: Lawyer): string | number | boolean {
  if (field.defaultValue !== undefined) return field.defaultValue;
  const today = new Date();
  if (field.preset === "matter") return matter?.matterNumber ?? "";
  if (field.preset === "lawyer") return `${lawyer.firstName} ${lawyer.lastName}`;
  if (field.preset === "today") return today.toISOString().slice(0, 10);
  if (field.preset === "yearStart") return `${today.getFullYear()}-01-01`;
  if (field.preset === "year") return today.getFullYear();
  return field.kind === "checkbox" ? false : "";
}

function initialValues(tool: FirmToolDefinition, matter: Matter | null, lawyer: Lawyer) {
  return Object.fromEntries(tool.fields.map((field) => [field.key, presetValue(field, matter, lawyer)]));
}

function inputFromValues(tool: FirmToolDefinition, values: Record<string, string | number | boolean>) {
  return Object.fromEntries(tool.fields.flatMap((field) => {
    const value = values[field.key];
    if (field.kind !== "checkbox" && value === "") return [];
    if (field.kind === "number") return [[field.key, Number(value)]];
    return [[field.key, value]];
  }));
}

function downloadCsv(presentation: ToolPresentation) {
  const columns = presentation.content.columns ?? [];
  const escape = (value: unknown) => `"${String(value ?? "").replaceAll('"', '""')}"`;
  const csv = [
    columns.map((column) => escape(column.label)).join(","),
    ...(presentation.content.rows ?? []).map((row) => columns.map((column) => escape(row[column.key])).join(",")),
  ].join("\n");
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = `${presentation.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "report"}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

function InteractivePresentation({ presentation }: { presentation: ToolPresentation }) {
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState<{ key: string; direction: "asc" | "desc" } | null>(null);
  const rows = presentation.content.rows ?? [];
  const columns = presentation.content.columns ?? [];
  const visibleRows = useMemo(() => {
    const query = filter.trim().toLocaleLowerCase();
    const filtered = query
      ? rows.filter((row) => Object.values(row).some((value) => String(value ?? "").toLocaleLowerCase().includes(query)))
      : [...rows];
    if (!sort) return filtered;
    return filtered.sort((left, right) => {
      const comparison = String(left[sort.key] ?? "").localeCompare(String(right[sort.key] ?? ""), undefined, { numeric: true });
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [filter, rows, sort]);

  useEffect(() => {
    setFilter("");
    setSort(null);
  }, [presentation]);

  return (
    <section className="firm-report-app">
      <div className="firm-report-heading">
        <div><span>PARAPLUS</span><h3>{presentation.title}</h3>{presentation.description && <p>{presentation.description}</p>}</div>
        <b>CURRENT</b>
      </div>
      {presentation.content.metrics && (
        <div className="firm-report-metrics">
          {presentation.content.metrics.map((metric) => <div key={metric.label}><span>{metric.label}</span><strong>{metric.value}</strong>{metric.detail && <small>{metric.detail}</small>}</div>)}
        </div>
      )}
      {columns.length > 0 && (
        <>
          <div className="firm-report-controls">
            <label><span>Search</span><input value={filter} onChange={(event) => setFilter(event.target.value)} placeholder="Search this list" /></label>
            <span>{visibleRows.length} of {rows.length}</span>
            <button type="button" onClick={() => { setFilter(""); setSort(null); }}>Clear</button>
            <button type="button" onClick={() => downloadCsv(presentation)}>Download CSV</button>
          </div>
          <div className="firm-report-table-wrap">
            <table>
              <thead><tr>{columns.map((column) => <th key={column.key}><button type="button" onClick={() => setSort((current) => current?.key === column.key ? { key: column.key, direction: current.direction === "asc" ? "desc" : "asc" } : { key: column.key, direction: "asc" })}>{column.label}{sort?.key === column.key ? sort.direction === "asc" ? " ↑" : " ↓" : ""}</button></th>)}</tr></thead>
              <tbody>
                {visibleRows.map((row, index) => <tr key={index}>{columns.map((column) => <td key={column.key} data-label={column.label}>{String(row[column.key] ?? "")}</td>)}</tr>)}
                {visibleRows.length === 0 && <tr><td colSpan={columns.length}>No matching rows.</td></tr>}
              </tbody>
            </table>
          </div>
        </>
      )}
      {presentation.content.items && (
        <div className="firm-report-items">
          {presentation.content.items.map((item) => (
            <div key={item.id}>
              <i className={item.status ?? "pending"}>{item.status === "attention" ? "!" : "•"}</i>
              <span><strong>{item.label}</strong>{item.detail && <small>{item.detail}</small>}</span>
            </div>
          ))}
        </div>
      )}
      {presentation.content.links && (
        <div className="firm-report-links">
          {presentation.content.links.map((link) => (
            <a key={`${link.url}:${link.label}`} href={link.url} target="_blank" rel="noreferrer">
              <strong>{link.label}</strong>
              {link.detail && <small>{link.detail}</small>}
            </a>
          ))}
        </div>
      )}
    </section>
  );
}

function ToolWorkspace({
  tool,
  capability,
  matter,
  lawyer,
  onClose,
}: {
  tool: FirmToolDefinition;
  capability: Capability;
  matter: Matter | null;
  lawyer: Lawyer;
  onClose: () => void;
}) {
  const [values, setValues] = useState(() => initialValues(tool, matter, lawyer));
  const [result, setResult] = useState<CapabilityResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const hasOptions = tool.fields.length > 0;
  const isAccountTool = tool.context === "accounts";

  const run = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const next = await api.invokeCapability(tool.name, inputFromValues(tool, values), capability.destructive && confirmed);
      setResult(next);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The tool could not be run.");
    } finally {
      setBusy(false);
    }
  }, [busy, capability.destructive, confirmed, tool, values]);

  useEffect(() => {
    if (!hasOptions && !capability.destructive && !result && !busy) {
      void run();
    }
  }, [busy, capability.destructive, hasOptions, result, run]);

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void run();
  };

  return (
    <div className="firm-tools-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="firm-tools-workspace" role="dialog" aria-modal="true" aria-label={tool.label}>
        <header>
          <div><span>{isAccountTool ? "ACCOUNT MANAGEMENT" : "FIRM TOOLS"}</span><h2>{tool.label}</h2><p>{tool.description}</p></div>
          <button type="button" onClick={onClose} aria-label="Close firm tool">×</button>
        </header>
        <div className={`firm-tools-workspace-body${hasOptions ? "" : " no-options"}${isAccountTool ? " account-context" : ""}`}>
          {hasOptions && <form className="firm-tool-form" onSubmit={submit}>
            <div className="firm-tool-form-heading">
              <strong>{isAccountTool ? "Account information" : "Options"}</strong>
              <small>{isAccountTool ? `Enter the information below, then choose ${tool.actionLabel ?? "View accounts"}.` : "Runs directly—no channel message is created."}</small>
            </div>
            {tool.fields.map((field) => field.kind === "checkbox" ? (
              <label className="firm-tool-check" key={field.key}>
                <input type="checkbox" checked={Boolean(values[field.key])} onChange={(event) => setValues({ ...values, [field.key]: event.target.checked })} />
                <span>{field.label}</span>
              </label>
            ) : (
              <label className={field.kind === "textarea" ? "wide" : ""} key={field.key}>
                <span>{field.label}{field.required ? " *" : ""}</span>
                {field.kind === "select" ? (
                  <select required={field.required} value={String(values[field.key] ?? "")} onChange={(event) => setValues({ ...values, [field.key]: event.target.value })}>
                    <option value="">{field.placeholder ?? "All / default"}</option>
                    {field.options?.map((option) => <option value={option} key={option}>{option.replace(/([a-z])([A-Z])/g, "$1 $2")}</option>)}
                  </select>
                ) : field.kind === "textarea" ? (
                  <textarea required={field.required} value={String(values[field.key] ?? "")} placeholder={field.placeholder} onChange={(event) => setValues({ ...values, [field.key]: event.target.value })} />
                ) : (
                  <input type={field.kind} step={field.kind === "number" ? "any" : undefined} required={field.required} value={String(values[field.key] ?? "")} placeholder={field.placeholder} onChange={(event) => setValues({ ...values, [field.key]: event.target.value })} />
                )}
              </label>
            ))}
            {capability.destructive && <label className="firm-tool-confirm"><input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span>{tool.confirmationLabel ?? "I have reviewed the inputs and want to run this action."}</span></label>}
            {error && <p className="firm-tool-error">{error}</p>}
            <button className="firm-tool-run" disabled={busy || (capability.destructive && !confirmed)}>{busy ? "Running…" : tool.actionLabel ?? (result ? "Refresh" : "Run tool")}</button>
          </form>}
          <div className="firm-tool-result">
            {!hasOptions && <div className="firm-tool-result-actions"><button type="button" onClick={() => void run()} disabled={busy}>{busy ? "Refreshing…" : "Refresh"}</button></div>}
            {!hasOptions && error && <p className="firm-tool-error">{error}</p>}
            {result?.kind === "text" && result.presentation && <InteractivePresentation presentation={result.presentation} />}
            {result?.kind === "text" && !result.presentation && <section className="firm-tool-message"><span>PARAPLUS</span><h3>{tool.label}</h3><pre>{result.text}</pre></section>}
            {result?.kind === "approval_required" && <section className="firm-tool-message warning"><h3>Confirmation required</h3><p>{result.message}</p></section>}
            {!result && !hasOptions && !error && <div className="firm-tool-empty"><span>{tool.icon}</span><h3>Loading…</h3></div>}
            {!result && hasOptions && <div className="firm-tool-empty"><span>{tool.icon}</span><h3>{isAccountTool ? "Ready" : "Ready to run"}</h3><p>{isAccountTool ? "Complete the account information on the left to continue." : "Choose any options, then run this tool. Results stay here instead of becoming channel messages."}</p></div>}
          </div>
        </div>
      </section>
    </div>
  );
}

export function FirmToolsMenu({
  matter,
  lawyer,
  onClose,
}: {
  matter: Matter | null;
  lawyer: Lawyer;
  onClose: () => void;
}) {
  const [capabilities, setCapabilities] = useState<Capability[] | null>(null);
  const [active, setActive] = useState<FirmToolDefinition | null>(null);
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.capabilities()
      .then((result) => setCapabilities(result.capabilities))
      .catch((caught: Error) => setError(caught.message));
  }, []);

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => window.removeEventListener("keydown", closeOnEscape);
  }, [onClose]);

  const available = useMemo(() => new Map(capabilities?.map((capability) => [capability.name, capability]) ?? []), [capabilities]);
  const filteredGroups = useMemo(() => groups.map((group) => ({
    ...group,
    tools: group.tools.filter((tool) => available.has(tool.name) && `${tool.label} ${tool.description}`.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase())),
  })).filter((group) => group.tools.length > 0), [available, query]);

  if (active) {
    const capability = available.get(active.name);
    return capability ? <ToolWorkspace tool={active} capability={capability} matter={matter} lawyer={lawyer} onClose={onClose} /> : null;
  }

  return (
    <div className="firm-tools-popover-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <div className="firm-tools-flyout" role="dialog" aria-label="Firm tools">
      <header><div><span>PARAPLUS</span><h2>Firm tools</h2><p>Work outside the conversation.</p></div><button type="button" onClick={onClose} aria-label="Close firm tools">×</button></header>
      <label className="firm-tools-search"><span>⌕</span><input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Find reports, billing, time…" /></label>
      <div className="firm-tools-groups">
        {!capabilities && !error && <p className="firm-tools-loading">Loading available tools…</p>}
        {error && <p className="firm-tool-error">{error}</p>}
        {filteredGroups.map((group) => <section key={group.label}><h3>{group.label}</h3><div>{group.tools.map((tool) => <button type="button" key={tool.name} onClick={() => setActive(tool)}><i>{tool.icon}</i><span><strong>{tool.label}</strong><small>{tool.description}</small></span><b>›</b></button>)}</div></section>)}
        {capabilities && filteredGroups.length === 0 && <p className="firm-tools-loading">No available tools match that search.</p>}
      </div>
      <footer>Only tools available to your role are shown.</footer>
      </div>
    </div>
  );
}
